import React from 'react'
import { connect } from 'react-redux'
import { withTranslation } from 'react-i18next';
import Map from './Map';
import JSONDataContext from "../../context"
import {build_router_infos} from "../../utils/Router"

import {
	loader_global_hide,
	set_map_unloaded
} from '../../actions'


class MapBox extends React.Component {
	/*
		Wrapper around the Fabricjs
		Map.
	*/
	
	constructor(props) {
		super(props);
		// reference to container, where we add the map element
		this.map_container = React.createRef(null);
		
		this.map = null;
		
		// we need a local state for the mouse handler setup tracking
		// just use map_loaded and json_loaded can lead to a race condition
		this.state = {
			setup_mouse_handlers_done: false
		}
	}
	
	componentDidMount() {
		/*
			add the class canvas element to the map_container reference
			and call setup
		*/
		if(!this.map) {
		    this.map = new Map();
    		this.map_container.current.appendChild(this.map.canvas_element);
    		this.map.setup();
    		this.props.loader_global_hide();
    	}
	}
	
	componentWillUnmount() {
		if (this.map) {
	        if (this.map.fabric_canvas) {
				this.map.remove_mouse_handlers()
	            this.map.fabric_canvas.dispose();
	            this.map.fabric_canvas = null;
	        }
	
	        const container = this.map_container.current;
	        const canvas = this.map.canvas_element;
	        
	        if (container && canvas && container.contains(canvas)) {
	            container.removeChild(canvas);
	        }
	
	        this.map = null;
	        this.map_container.current = null
	        // as this is not a local state, we need to update it
	        this.props.set_map_unloaded();
	    }
	}
	
	componentDidUpdate = (prev_props) => {
		/*
			the main work happens here...
		*/
		if (!this.map || !this.map.fabric_canvas || !this.map.svg_main_group) {
	        return; 
	    }
		
		const {
			map_loaded,
			json_loaded,
			pathname,
			panel_detail_is_open,
			panel_start_overlay_is_open,
			panel_detail_space,
			panel_header_height,
			client_type,
			window_width,
			window_height,
			orientation,
			is_authenticated,
			show_small_station_labels,
			show_medium_station_labels,
			show_large_station_labels,
			show_line_labels,
			override_color_bg,
			override_color_label,
			mode,
			reset_trigger
		} = this.props
		
		const router_infos = build_router_infos(pathname)
		const prev_router_infos = build_router_infos(prev_props.pathname)
		
		const is_intern = router_infos.type === 'intern' && is_authenticated ? true : false

		this.map.update_intern_state(is_intern)
		this.map.update_client_type(client_type)
		this.map.update_router_infos(router_infos)
		this.map.update_panel_spaces(panel_detail_space, panel_header_height)
		this.map.update_start_panel_state(panel_start_overlay_is_open)
		
		/* 
		   	we can not use prev_props.json_loaded, we need a local state
		   	else we have a race condition which can lead to unset json
		   	and unset mouse handlers
		*/
		if (json_loaded && !this.state.setup_mouse_handlers_done && map_loaded) {
			if(is_intern) { // show everything in disabled, grey state
				this.map.intern_reset_all()
				this.map.intern_update_intern_get_to_station_origins_json_fnc(this.intern_get_to_station_origins_json)
			}
			//actually we should only add over and out mouse handlers if not mobile
			this.map.setup_mouse_handlers(this.context.general.all_lines, this.context.general.all_stations)
			this.setState({setup_mouse_handlers_done: true})
		}
		
		// all loaded
		if(json_loaded && map_loaded) {
			if(window_width !== prev_props.window_width || window_height !== prev_props.window_height) {
				if(client_type === 'desktop') { // only desktop reacts on a resize. Mobiles fire resizes also for OSK or other stuff on which we do not want to react!
					this.map.zoom_check_map_resize(this.map_container.current.offsetWidth, this.map_container.current.offsetHeight)
				}
			}
			if(orientation !== prev_props.orientation) { // mobile orientation handling
				console.log('orientation changed, adjust map')
				this.map.zoom_check_map_resize(window.innerWidth, window.innerHeight)
			}
			if(router_infos.type === 'intern') {
				if(prev_props.reset_trigger !== reset_trigger) {
					this.map.intern_reset_all()
				}
				if(prev_props.mode !== mode) {
					this.map.intern_update_mode(mode)
				}
				if(prev_props.show_small_station_labels !== show_small_station_labels) {
					this.map.intern_update_show_station_labels(show_small_station_labels, 'small')
				}
				if(prev_props.show_medium_station_labels !== show_medium_station_labels) {
					this.map.intern_update_show_station_labels(show_medium_station_labels, 'medium')
				}
				if(prev_props.show_large_station_labels !== show_large_station_labels) {
					this.map.intern_update_show_station_labels(show_large_station_labels, 'large')
				}
				if(prev_props.show_line_labels !== show_line_labels) {
					this.map.intern_update_show_line_labels(show_line_labels)
				}
				if(prev_props.override_color_bg !== override_color_bg) {
					this.map.intern_update_override_color(override_color_bg, override_color_label)
				}
			}
			if(router_infos.type === 'home') {
				if(!prev_props.map_loaded) {
					this.map.initial_zoom_move()
				}
				if(prev_router_infos.type === 'station') {
					this.map.reset_all_highlight_station()
					this.map.reset_line_highlight()
				} else if(prev_router_infos.type === 'line') {
					this.map.reset_line_highlight()
				}
			}
			if(router_infos.type === 'line') {
				if(prev_router_infos.type === 'station') {
					this.map.reset_all_highlight_station()
				}
				this.map.highlight_line(router_infos.line_slug);
				if(panel_detail_is_open) { // animation are started when the detail panel is open
					this.map.zoom_highlighted_line(router_infos.line_slug);
				}
			}
			if(router_infos.type === 'station') {
                const from_station_origins_json = this.context.origins[router_infos.from_slug]
				//let from_to_highlighted_tracks_stations = [] // f24+: we use this get the proper zoom_box
                if(from_station_origins_json !== undefined) {
                    this.map.update_from_station_origins_json(from_station_origins_json)
                } else {
                    this.map.update_from_station_origins_json({})
                }
                // reset all, does not "blink"
                this.map.reset_all_highlight_station()
                // do station highlight
                this.map.highlight_station(router_infos.from_slug, 'from')
                if(router_infos.to_slug !== undefined) {
                    const to_station_origins_json = this.context.origins[router_infos.to_slug]
                    if(to_station_origins_json !== undefined) {
                        this.map.update_to_station_origins_json(to_station_origins_json)
                    } else {
                        this.map.update_to_station_origins_json({})
                    }
                    this.map.highlight_station(router_infos.to_slug, 'to')
                    // highlight all lines connected, both stations
                    this.map.highlight_all_from_to_station_lines(router_infos.from_slug, router_infos.to_slug)
                } else {
                    // highlight all lines connected, only to "from" station
                    this.map.highlight_all_from_station_lines()
                }
                if(panel_detail_is_open) { // animation are started when the detail panel is open
                    if(router_infos.to_slug !== undefined) {
						// @Update f24 (Nov 2023): we use all stations around highlighted tracks to find the max stretch values for zoom_box
						if(from_station_origins_json.connected_stations.hasOwnProperty(router_infos.to_slug)){
					 		this.map.zoom_highlighted_tracks(from_station_origins_json.connected_stations[router_infos.to_slug].track_highlights_affected_stations);
						}else{
							this.map.zoom_highlighted_stations(router_infos.from_slug, router_infos.to_slug)
						}
                    } else { // single station, check if visible, if not, center and zoom it
                        this.map.zoom_not_visible_station(router_infos.from_slug)
                    }
                }
			}
		}
	}
	
	/*
		special function to allow a call from map to get json from the context.
		We only need this method in intern mode.
		in public mode this is done over store change/update.
	*/
	intern_get_to_station_origins_json = (destination_code) => {
		const to_station_origins_json = this.context.origins[destination_code]
		if(to_station_origins_json !== undefined) {
			return to_station_origins_json
		} else {
			return {}
		}
	}
	
	render = () => {
		return (
			<div className="map-container" ref={this.map_container}></div>
		)
	}
}

const map_state_to_props = state => {
	return {
		panel_detail_space: state.general.panels.panel_detail_space,
		panel_header_height: state.general.panels.panel_header_height,
		panel_detail_is_open: state.general.panels.panel_detail_is_open,
		panel_start_overlay_is_open: state.general.panels.panel_start_overlay_is_open,
		loader_global_show_count: state.general.ui.loader_global_show_count,
		map_loaded: state.general.data.map_loaded,
		json_loaded: state.general.data.json_loaded,
		pathname: state.router.location.pathname,
		client_type: state.general.ui.client_type,
		window_width: state.general.ui.window_width,
		window_height: state.general.ui.window_height,
		orientation: state.general.ui.orientation,
		is_authenticated: state.general.intern.is_authenticated,
		show_small_station_labels: state.general.intern_config.show_small_station_labels,
		show_medium_station_labels: state.general.intern_config.show_medium_station_labels,
		show_large_station_labels: state.general.intern_config.show_large_station_labels,
		show_line_labels: state.general.intern_config.show_line_labels,
		override_color_bg: state.general.intern_config.override_color_bg,
		override_color_label: state.general.intern_config.override_label,
		mode: state.general.intern_config.mode,
		reset_trigger: state.general.intern_config.reset_trigger
	}
}

const map_dispatch_to_props = dispatch => {
	return {
		loader_global_hide: () => {
			dispatch(loader_global_hide())
		},
		set_map_unloaded: () => {
			dispatch(set_map_unloaded())
		}
	}
}

MapBox.contextType = JSONDataContext;

// connect to redux store
MapBox = connect(
	map_state_to_props,
	map_dispatch_to_props
)(MapBox)

// add i18n context for the language
MapBox = withTranslation()(MapBox)

export default MapBox;
